import { apiClient } from "@api/client";
import { request, requestVoid } from "@api/request";
import type { components } from "@api/schema";

type SystemRole = components["schemas"]["SystemRole"];

/** A held role: clinic-facing label plus permission tier. */
export interface HeldRole {
  id: string;
  /** Clinic-facing label, e.g. "Practice Manager". */
  name: string;
  systemRole: SystemRole;
}

/** The signed-in user, as needed by the header. */
export interface ClinicUser {
  id: string;
  displayName: string;
  /** 2-digit per-clinic login code (their "username"). */
  code: string;
  /**
   * Primary role's label, what the header and admin roster show. Derived from
   * `roles[0]`, not a wire field: brian serves roles only, no singular role.
   */
  role: string;
  /**
   * Every role held, primary first (BE-2279); each grants access on its own.
   * Mirrors brian's `UserOut.roles`.
   */
  roles: HeldRole[];
  createdAt: string;
}

const holdsAny = (user: ClinicUser | null, tiers: readonly SystemRole[]) =>
  !!user?.roles.some((r) => tiers.includes(r.systemRole));

/**
 * True if this one role is the LEADERSHIP tier. The single place that tier is
 * named, so access checks and anything that presents a role differently for it
 * (the admin roster's emphasis) can't drift apart.
 */
export const isLeadershipRole = (role: HeldRole): boolean =>
  role.systemRole === "LEADERSHIP";

/**
 * True if any role held is LEADERSHIP, so admin access and task-visibility
 * scoping (BE-2089) can't drift apart.
 */
export const isLeadership = (user: ClinicUser | null): boolean =>
  !!user?.roles.some(isLeadershipRole);

/**
 * True if any role held is CLINICAL_DVM or LEADERSHIP (BE-2034). Mirrors brian's
 * `POST /views/{id}/sign` gate; its 403 is the backstop.
 */
export const canSign = (user: ClinicUser | null): boolean =>
  holdsAny(user, ["CLINICAL_DVM", "LEADERSHIP"]);

/**
 * Whether this user may enter the Admin area. The single access check shared by
 * the route guard (AdminShell), the user-menu Admin item, and the `G A` shortcut.
 */
export const canAccessAdmin = (user: ClinicUser | null): boolean =>
  isLeadership(user);

/**
 * Whether this user may open Visit Replay. Leadership-only for now — the
 * single check shared by the route guard (ReplayShell) and the PatientCard
 * entry link. Frontend-only gating; the API itself is not role-restricted.
 */
export const canAccessReplay = (user: ClinicUser | null): boolean =>
  isLeadership(user);

/**
 * Whether this user may open a visit's surfaced-to-seen report (BE-2151). Unlike
 * {@link canAccessReplay} the API enforces this too (403) — the check exists so
 * the entry point is hidden rather than left to fail, since this is per-user
 * behavioural data nobody opted into being measured by.
 */
export const canViewAttention = (user: ClinicUser | null): boolean =>
  isLeadership(user);

export interface UserClient {
  /** The user identified by the current user-scope token (GET /auth/user). */
  getCurrent(): Promise<ClinicUser>;
  /** All users in the clinic (GET /users) — used to resolve uuids to names. */
  list(): Promise<ClinicUser[]>;
  /** Set a user's PIN to an admin-chosen value (POST /users/{id}/reset-pin). */
  resetPin(userId: string, pin: string): Promise<void>;
}

const toClinicUser = (u: components["schemas"]["UserOut"]): ClinicUser => ({
  id: u.uuid,
  displayName: u.name,
  code: u.code,
  // brian orders `roles` primary-first and every user holds at least one, so the
  // display label is the first entry's name rather than a field of its own.
  role: u.roles[0].name,
  roles: u.roles.map((r) => ({
    id: r.uuid,
    name: r.name,
    systemRole: r.system_role,
  })),
  createdAt: u.created_at,
});

export const userClient: UserClient = {
  getCurrent: () =>
    request(() => apiClient.GET("/auth/user", {})).then(toClinicUser),

  list: () =>
    request(() => apiClient.GET("/users", {})).then((users) =>
      users.map(toClinicUser),
    ),

  resetPin: (userId, pin) =>
    requestVoid(() =>
      apiClient.POST("/users/{user_uuid}/reset-pin", {
        params: { path: { user_uuid: userId } },
        body: { pin },
      }),
    ),
};
